import React from "react";
import styles from "../SubjectSinglePage.module.css";
import type { SubjectItem, VisibleSubjectRow } from "../model/types";

type SubjectPickerPanelProps = {
  pickerMode: "tree" | "list";
  isSubjectSearchActive: boolean;
  visibleSubjectRows: VisibleSubjectRow[];
  footerSubject: SubjectItem | null;
  renderSubjectSearch: (label: string) => React.ReactNode;
  renderSubjectSearchEmpty: () => React.ReactNode;
  renderSubjectRow: (subject: SubjectItem, depth: number) => React.ReactNode;
  onSetPickerMode: (mode: "tree" | "list") => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onChooseSubject: (subjectId: number) => void;
  onSubjectTreeKeyDown: (event: React.KeyboardEvent) => void;
};

export function SubjectPickerPanel({
  pickerMode,
  isSubjectSearchActive,
  visibleSubjectRows,
  footerSubject,
  renderSubjectSearch,
  renderSubjectSearchEmpty,
  renderSubjectRow,
  onSetPickerMode,
  onExpandAll,
  onCollapseAll,
  onChooseSubject,
  onSubjectTreeKeyDown,
}: SubjectPickerPanelProps) {
  return (
    <section
      className={styles.pickerPanel}
      aria-labelledby="subject-single-picker-title"
    >
      <div className={styles.pickerHeader}>
        <div>
          <h1 id="subject-single-picker-title">Choose a subject</h1>
          <p>Focused browse starts compact: open one branch at a time, while search can still reveal matching paths.</p>
        </div>
        <div className={styles.pickerTools}>
          <span>View</span>
          <div
            className={styles.viewToggle}
            role="group"
            aria-label="Subject picker view mode"
          >
            <button
              type="button"
              className={pickerMode === "tree" ? styles.viewToggleActive : ""}
              onClick={() => onSetPickerMode("tree")}
              aria-label="Tree view"
            >
              ⎇
            </button>
            <button
              type="button"
              className={pickerMode === "list" ? styles.viewToggleActive : ""}
              onClick={() => onSetPickerMode("list")}
              aria-label="List view"
            >
              ☰
            </button>
          </div>
          <button
            type="button"
            className={styles.expandAllButton}
            onClick={onExpandAll}
          >
            Expand all
          </button>
          <button
            type="button"
            className={styles.expandAllButton}
            onClick={onCollapseAll}
          >
            Collapse all
          </button>
        </div>
      </div>

      {renderSubjectSearch("Search subjects")}
      <div
        className={`${styles.subjectTree} ${pickerMode === "list" && !isSubjectSearchActive ? styles.subjectTreeListMode : ""}`}
        role="tree"
        aria-label="Choose a subject. Use arrow keys to move through subjects. Press Enter to open the subject action menu."
        onKeyDown={onSubjectTreeKeyDown}
      >
        {visibleSubjectRows.length
          ? visibleSubjectRows.map((row) =>
              renderSubjectRow(row.subject, row.depth),
            )
          : renderSubjectSearchEmpty()}
      </div>

      <div className={styles.pickerFooter}>
        {footerSubject ? (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => onChooseSubject(footerSubject.id)}
            aria-label={`Open ${footerSubject.title} subject directly`}
          >
            Open {footerSubject.title} subject <span aria-hidden="true">→</span>
          </button>
        ) : (
          <span className={styles.emptyHint}>No subjects are available yet.</span>
        )}
      </div>
    </section>
  );
}
